import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import {
  computeNetworkQuality,
  downloadTest,
  measureInternetSpeed,
  ping,
  uploadTest,
  type ConnectionStatus,
  type InternetSpeedResult,
  type NetworkQuality,
  type NetworkSample,
  type SpeedTestResult,
} from '@/lib/network-speed';

import { NetworkMonitorContext, type NetworkTestStep } from './network-monitor-context';

const PING_INTERVAL_MS = 30_000;
const FULL_TEST_INTERVAL_MS = 30 * 60_000;
const IDLE_THRESHOLD_MS = 2 * 60_000;
const PERIODIC_DOWNLOAD_BYTES = 2_000_000;
const PERIODIC_UPLOAD_BYTES = 1_000_000;
const MANUAL_DOWNLOAD_BYTES = 5_000_000;
const MANUAL_UPLOAD_BYTES = 2_000_000;
const HISTORY_LIMIT = 10;
const ESCALATE_AFTER = 3;
const RECOVER_AFTER = 5;

const STATUS_RANK: Record<ConnectionStatus, number> = { good: 0, degraded: 1, critical: 2 };

const INITIAL_QUALITY: NetworkQuality = {
  status: 'good',
  latencyAvgMs: null,
  jitterMs: null,
  lossPercent: 0,
  downloadMbps: null,
  uploadMbps: null,
};

export function NetworkMonitorProvider({ children }: { children: ReactNode }) {
  const [online, setOnline] = useState(true);
  const [quality, setQuality] = useState<NetworkQuality>(INITIAL_QUALITY);
  const [history, setHistory] = useState<NetworkSample[]>([]);
  const [lastFullTest, setLastFullTest] = useState<SpeedTestResult | null>(null);
  const [lastInternetTest, setLastInternetTest] = useState<InternetSpeedResult | null>(null);
  const [isFullTestRunning, setIsFullTestRunning] = useState(false);
  const [fullTestProgress, setFullTestProgress] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const runningRef = useRef(false);
  const streakRef = useRef(0);
  const stableStatusRef = useRef<ConnectionStatus>('good');

  useEffect(() => {
    const markActivity = (): void => {
      lastActivityRef.current = Date.now();
    };
    window.addEventListener('pointerdown', markActivity);
    window.addEventListener('keydown', markActivity);
    return () => {
      window.removeEventListener('pointerdown', markActivity);
      window.removeEventListener('keydown', markActivity);
    };
  }, []);

  const [testStep, setTestStep] = useState<NetworkTestStep>('idle');
  const [testStepLabel, setTestStepLabel] = useState<string>('');

  const measureInternet = useCallback(
    (mode: 'full' | 'periodic') =>
      measureInternetSpeed(mode).catch(error => {
        console.error('Internet speed test failed:', error);
        return null;
      }),
    [],
  );

  const executeTest = useCallback(
    async (
      downloadBytes: number,
      uploadBytes: number,
      internetMode: 'full' | 'periodic' = 'full',
    ): Promise<void> => {
      if (runningRef.current) return;
      runningRef.current = true;
      setIsFullTestRunning(true);
      setFullTestProgress(0);

      try {
        // Step 1: Measure pure latency / ping to backend
        setTestStep('latency');
        setTestStepLabel('Vérification du temps de réponse avec le serveur...');
        setFullTestProgress(0.1);
        const latencyMs = await ping();

        // Step 2: Measure server download bandwidth (isolated, zero concurrency)
        setTestStep('download');
        setTestStepLabel('Téléchargement du bloc de test Cibli...');
        const downloadMbps = await downloadTest(downloadBytes, (ratio: number) => {
          // Download maps to 10% -> 60% of total progress
          setFullTestProgress(0.1 + ratio * 0.5);
        });

        // Step 3: Measure server upload bandwidth (isolated)
        setTestStep('upload');
        setTestStepLabel('Test du flux audio micro vers le serveur...');
        setFullTestProgress(0.65);
        const uploadMbps = await uploadTest(uploadBytes);
        setFullTestProgress(0.75);

        const serverResult: SpeedTestResult = {
          latencyMs,
          downloadMbps,
          uploadMbps,
          testedAt: new Date(),
        };
        setLastFullTest(serverResult);

        // Step 4: Measure real global Internet bandwidth (Cloudflare) sequentially
        setTestStep('internet');
        setTestStepLabel('Mesure du débit réel Internet mondial...');
        setFullTestProgress(0.8);
        const internetResult = await measureInternet(internetMode);
        if (internetResult) {
          setLastInternetTest(internetResult);
        }
        setFullTestProgress(1);
      } catch (error) {
        console.error('Speed test failed:', error);
      } finally {
        setTestStep('idle');
        setTestStepLabel('');
        runningRef.current = false;
        setIsFullTestRunning(false);
      }
    },
    [measureInternet],
  );

  const runManualTest = useCallback(
    () => executeTest(MANUAL_DOWNLOAD_BYTES, MANUAL_UPLOAD_BYTES),
    [executeTest],
  );

  // Continuous ping loop
  useEffect(() => {
    let cancelled = false;

    const tick = async (): Promise<void> => {
      // Pause background pings while a throughput speedtest is actively running
      if (runningRef.current) return;

      let sample: NetworkSample;
      try {
        sample = { latencyMs: await ping(), at: Date.now() };
      } catch {
        sample = { latencyMs: null, at: Date.now() };
      }
      if (cancelled) return;

      setOnline(sample.latencyMs !== null);
      setHistory(prev => {
        const next = [...prev.slice(-(HISTORY_LIMIT - 1)), sample];

        setQuality(() => {
          const measured = computeNetworkQuality(
            next,
            lastFullTest?.downloadMbps ?? null,
            lastFullTest?.uploadMbps ?? null,
          );

          if (sample.latencyMs === null) {
            streakRef.current = 0;
            stableStatusRef.current = 'critical';
            return { ...measured, status: 'critical' };
          }

          if (STATUS_RANK[measured.status] > STATUS_RANK[stableStatusRef.current]) {
            streakRef.current += 1;
            if (streakRef.current >= ESCALATE_AFTER) {
              stableStatusRef.current = measured.status;
              streakRef.current = 0;
            }
          } else if (STATUS_RANK[measured.status] < STATUS_RANK[stableStatusRef.current]) {
            streakRef.current += 1;
            if (streakRef.current >= RECOVER_AFTER) {
              stableStatusRef.current = measured.status;
              streakRef.current = 0;
            }
          } else {
            streakRef.current = 0;
          }

          return { ...measured, status: stableStatusRef.current };
        });

        return next;
      });
    };

    void tick();
    const id = setInterval(tick, PING_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [lastFullTest?.downloadMbps, lastFullTest?.uploadMbps]);

  // Periodic throughput test, only when the booth is idle
  useEffect(() => {
    const id = setInterval(() => {
      const isIdle = Date.now() - lastActivityRef.current > IDLE_THRESHOLD_MS;
      if (isIdle && document.visibilityState === 'visible') {
        void executeTest(PERIODIC_DOWNLOAD_BYTES, PERIODIC_UPLOAD_BYTES, 'periodic');
      }
    }, FULL_TEST_INTERVAL_MS);
    return () => clearInterval(id);
  }, [executeTest]);

  const value = useMemo(
    () => ({
      online,
      quality,
      history,
      lastFullTest,
      lastInternetTest,
      isFullTestRunning,
      fullTestProgress,
      testStep,
      testStepLabel,
      runManualTest,
    }),
    [
      online,
      quality,
      history,
      lastFullTest,
      lastInternetTest,
      isFullTestRunning,
      fullTestProgress,
      testStep,
      testStepLabel,
      runManualTest,
    ],
  );

  return <NetworkMonitorContext.Provider value={value}>{children}</NetworkMonitorContext.Provider>;
}
